import React, { useState } from 'react'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import { useIsMobile } from 'lib/hooks'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from 'components/material-symbol/icons/Close'
import ClearIcon from 'components/material-symbol/icons/Clear'
import SearchOutlinedIcon from 'components/material-symbol/icons/Search'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { gql, useQuery } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useChatContext } from 'stream-chat-react'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Divider from '@material-ui/core/Divider'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import { useAuth } from 'lib/auth'
import { useFindOrCreateChannelWithUserIds } from 'lib/stream-messaging/hooks'
import Dialog from 'components/dialog'
import FilterDropdown from 'components/filter-dropdown'
import MessagingCheckbox from '../messaging-checkbox'
import MessagingContactAvatar from '../messaging-contact-avatar'
import useStyles from './styles'
import { GroupEventAudienceMessageForm } from '../group-event-audience-message-dialog'

const CONVERSATION_MODES = {
  CONTACTS: 'contacts',
  GROUP_EVENT: 'group-event',
}

const GET_MESSAGING_CONTACTS = gql`
  query MessagingContacts {
    messagingContacts {
      type
      id
      name
      image
      externalId
      description
      sources
    }
    messagingContactSource: __type(name: "MessagingContactSource") {
      enumValues {
        name
        description
      }
    }
  }
`

const NewConversationDialog = ({ trigger }) => {
  const fullScreen = useIsMobile()
  const classes = useStyles()
  const findOrCreateChannelWithUserIds = useFindOrCreateChannelWithUserIds()
  const { setActiveChannel } = useChatContext()
  const { enqueueSnackbar } = useSnackbar()
  const { data, loading, error, refetch } = useQuery(GET_MESSAGING_CONTACTS)
  const [selectedFilter, setSelectedFilter] = useState('ALL')
  const [searchFieldText, setSearchFieldText] = useState('')
  const [selectedContactExternalIds, setSelectedContactExternalIds] = useState(
    [],
  )
  const [conversationMode, setConversationMode] = useState(
    CONVERSATION_MODES.CONTACTS,
  )
  const { isConsumer, isCoach } = useAuth()

  const toggleContactSelection = contact => {
    const currentIndex = selectedContactExternalIds.indexOf(contact.externalId)
    const newSelection = [...selectedContactExternalIds]

    if (currentIndex === -1) {
      newSelection.push(contact.externalId)
    } else {
      newSelection.splice(currentIndex, 1)
    }

    setSelectedContactExternalIds(newSelection)
  }

  const isContactSelected = contact =>
    selectedContactExternalIds.includes(contact.externalId)

  const handleFilterChange = newFilter => {
    setSelectedFilter(newFilter)
    setSelectedContactExternalIds([])
  }

  const messagingContacts = data?.messagingContacts ?? []
  const messagingContactSource = data?.messagingContactSource

  const filterOptions = Array.from(
    new Set(messagingContacts.flatMap(contact => contact.sources)),
  ).map(opt => messagingContactSource?.enumValues.find(e => e.name === opt))
  filterOptions.unshift({
    name: 'ALL',
    description: 'All Contacts',
    __typename: '__EnumValue',
  })

  const selectedFilterValue = selectedFilter

  const filteredContacts = messagingContacts
    .filter(
      contact =>
        !searchFieldText ||
        contact.name.toLowerCase().includes(searchFieldText.toLowerCase()),
    )
    .filter(
      contact =>
        !selectedFilterValue.length ||
        selectedFilterValue.includes('ALL') ||
        contact.sources.some(s => selectedFilterValue.includes(s)),
    )

  const allContactsSelected =
    filteredContacts.length > 0 &&
    selectedContactExternalIds.length === filteredContacts.length

  const toggleSelectAll = () => {
    if (!allContactsSelected) {
      setSelectedContactExternalIds(
        filteredContacts.map(contact => contact.externalId),
      )
    } else {
      setSelectedContactExternalIds([])
    }
  }

  const renderContactsContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )
    }

    if (error) {
      return (
        <Box>
          <Typography variant="body1" gutterBottom>
            Unable to load contacts
          </Typography>
          <Button variant="contained" color="primary" onClick={() => refetch()}>
            Reload
          </Button>
        </Box>
      )
    }

    return (
      <>
        <Box className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            color="primary"
            fullWidth
            onChange={e => setSearchFieldText(e.target.value)}
            placeholder="Search Contacts"
            value={searchFieldText}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchFieldText.length ? (
                <InputAdornment
                  position="end"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSearchFieldText('')}
                >
                  <ClearIcon fontSize="small" />
                </InputAdornment>
              ) : null,
            }}
          />
          {filterOptions.length > 2 && (
            <Box className={classes.filterButton}>
              <FilterDropdown
                filterOptions={filterOptions}
                selectedFilter={selectedFilter}
                setSelectedFilter={handleFilterChange}
              />
            </Box>
          )}
        </Box>

        {filteredContacts.length > 0 ? (
          <List className={classes.contactList} disablePadding>
            <ListItem
              button
              className={classes.listHeader}
              disableGutters
              disabled={filteredContacts.length === 0}
              onClick={toggleSelectAll}
            >
              <MessagingCheckbox
                checked={allContactsSelected}
                className={classes.contactCheckbox}
                color="primary"
                disabled={filteredContacts.length === 0}
                indeterminate={
                  selectedContactExternalIds.length > 0 && !allContactsSelected
                }
                inputProps={{ 'aria-label': 'Select all contacts' }}
                onChange={toggleSelectAll}
                onClick={event => event.stopPropagation()}
              />
              <Box className={classes.listHeaderText}>
                <ListItemText
                  classes={{ primary: classes.listHeaderLabel }}
                  primary={
                    selectedContactExternalIds.length > 0
                      ? `${selectedContactExternalIds.length} selected`
                      : 'Select all contacts'
                  }
                />
              </Box>
            </ListItem>
            <Divider className={classes.listHeaderDivider} component="li" />
            {filteredContacts.map(contact => {
              const key = `${contact.type}-${contact.id}`
              return (
                <ListItem
                  key={key}
                  button
                  className={classes.contactListItem}
                  disableGutters
                  onClick={() => toggleContactSelection(contact)}
                >
                  <MessagingCheckbox
                    className={classes.contactCheckbox}
                    color="primary"
                    onChange={() => toggleContactSelection(contact)}
                    checked={isContactSelected(contact)}
                    tabIndex={-1}
                    inputProps={{ 'aria-labelledby': key }}
                    onClick={event => event.stopPropagation()}
                  />
                  <MessagingContactAvatar
                    className={classes.contactAvatar}
                    image={contact.image}
                    name={contact.name}
                  />
                  <Box className={classes.contactRowText}>
                    <ListItemText
                      id={key}
                      primary={contact.name}
                      secondary={contact.description}
                    />
                  </Box>
                </ListItem>
              )
            })}
          </List>
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant="body1">No contacts found</Typography>
            {isCoach && selectedFilterValue.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                Your students and leads will appear here once they have been
                added
              </Typography>
            )}
            {isConsumer && selectedFilterValue.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                It doesn't look like you have any contacts yet
              </Typography>
            )}
            {selectedFilterValue.length !== 0 && (
              <Typography variant="body2" color="textSecondary">
                Try removing some of your filters and search again
              </Typography>
            )}
          </Box>
        )}
      </>
    )
  }

  return (
    <Dialog
      trigger={trigger}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.dialogPaper }}
    >
      {({ closeDialog }) => {
        const createConversation = async () => {
          const channel = await findOrCreateChannelWithUserIds(
            selectedContactExternalIds,
          )
          if (channel) {
            setActiveChannel(channel)
            closeDialog && closeDialog()
          } else {
            enqueueSnackbar(
              'Unable to create or join conversation. Please try again later.',
              {
                variant: 'error',
              },
            )
          }
        }

        return (
          <>
            <DialogTitle disableTypography className={classes.dialogTitle}>
              <Typography className={classes.title}>New Message</Typography>
              <IconButton
                aria-label="Close"
                className={classes.closeButton}
                onClick={closeDialog}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent className={classes.content}>
              {isCoach && (
                <Tabs
                  value={conversationMode}
                  onChange={(_event, value) => setConversationMode(value)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Contacts" value={CONVERSATION_MODES.CONTACTS} />
                  <Tab
                    label="Group event"
                    value={CONVERSATION_MODES.GROUP_EVENT}
                  />
                </Tabs>
              )}

              {conversationMode === CONVERSATION_MODES.GROUP_EVENT ? (
                <GroupEventAudienceMessageForm
                  onCancel={closeDialog}
                  onSuccess={closeDialog}
                  onChannelCreated={channel => setActiveChannel(channel)}
                />
              ) : (
                renderContactsContent()
              )}
            </DialogContent>

            {conversationMode === CONVERSATION_MODES.CONTACTS && (
              <DialogActions className={classes.dialogActions}>
                <Button onClick={closeDialog}>Cancel</Button>
                <Button
                  className={classes.createButton}
                  variant="contained"
                  color="primary"
                  onClick={createConversation}
                  disabled={selectedContactExternalIds.length === 0}
                >
                  Create
                </Button>
              </DialogActions>
            )}
          </>
        )
      }}
    </Dialog>
  )
}

export default NewConversationDialog
